<script lang="ts">
  import chattyLogo from "../assets/logo.png";

  interface Props {
    error?: string | null;
    submitting?: boolean;
    microsoftEnabled?: boolean;
    authConfigLoading?: boolean;
    dashboardUrl?: string;
    appName?: string;
    onEmailLogin: (email: string, password: string) => void | Promise<void>;
    onMicrosoftLogin: () => void | Promise<void>;
    onRetry?: () => void | Promise<void>;
  }

  let {
    error = null,
    submitting = false,
    microsoftEnabled = false,
    authConfigLoading = false,
    dashboardUrl = "http://localhost:3012",
    appName = "Chatty Office",
    onEmailLogin,
    onMicrosoftLogin,
    onRetry,
  }: Props = $props();

  let email = $state("");
  let password = $state("");

  function friendlyError(value: string | null): string | null {
    if (!value) return null;
    const messages: Record<string, string> = {
      invalid_credentials: "Invalid email or password.",
      account_disabled: "This account is disabled. Contact your administrator.",
      social_account: "This account uses social sign-in. Use the matching sign-in button.",
      microsoft_sso_not_configured: "Microsoft 365 sign-in is not configured for this workspace.",
      invalid_microsoft_token: "Microsoft authentication failed. Please try again.",
      auth_config_unavailable: "Could not load sign-in options. You can still try email sign-in.",
      gateway_unavailable: "Chatty gateway is unavailable. You can retry in a moment.",
      login_failed: "Sign-in failed. Please try again.",
      microsoft_login_failed: "Microsoft sign-in failed. Please try again.",
      popup_window_error: "The Microsoft sign-in popup was blocked. Allow popups and try again.",
      user_cancelled: "Microsoft sign-in was cancelled.",
      needs_signup: "No Chatty workspace exists for this Microsoft account yet.",
      request_access: "This Microsoft account needs access approval before signing in.",
    };
    return messages[value] ?? value.split("_").join(" ");
  }

  function submitEmail() {
    void onEmailLogin(email.trim(), password);
  }

  function openDashboard(path = "") {
    window.open(`${dashboardUrl}${path}`, "_blank", "noopener,noreferrer");
  }
</script>

<div class="chatty-login-shell chatty-font-scope">
  <div class="chatty-login-card-wrap">
    <div class="chatty-brand-block">
      <div class="chatty-logo-frame">
        <img src={chattyLogo} alt={appName} />
      </div>
      <h1 dir="auto" class="chatty-dir-auto">{appName}</h1>
      <p>Sign in to your account</p>
    </div>

    <section class="chatty-card" aria-label="Chatty sign in">
      <form
        class="chatty-form"
        onsubmit={(event) => {
          event.preventDefault();
          submitEmail();
        }}
      >
        <label>
          <span>Email</span>
          <input
            type="email"
            dir="ltr"
            autocomplete="email"
            placeholder="you@company.com"
            bind:value={email}
            disabled={submitting}
            required
          />
        </label>

        <label>
          <span>Password</span>
          <input
            type="password"
            dir="ltr"
            autocomplete="current-password"
            placeholder="••••••••"
            bind:value={password}
            disabled={submitting}
            required
          />
        </label>

        {#if friendlyError(error)}
          <div class="chatty-error chatty-dir-auto" role="alert" dir="auto">
            <span>{friendlyError(error)}</span>
            {#if error === "gateway_unavailable" && onRetry}
              <button type="button" onclick={() => void onRetry?.()}>Retry</button>
            {/if}
          </div>
        {/if}

        <button class="chatty-primary" type="submit" disabled={submitting || !email || !password}>
          {submitting ? "Signing in…" : "Sign In"}
        </button>
      </form>

      {#if authConfigLoading}
        <div class="chatty-auth-loading chatty-dir-auto" dir="auto">Checking Microsoft 365 sign-in…</div>
      {:else if microsoftEnabled}
        <div class="chatty-divider" aria-hidden="true">
          <span></span>
          <em>or</em>
          <span></span>
        </div>

        <button class="chatty-microsoft" type="button" disabled={submitting} onclick={() => void onMicrosoftLogin()}>
          <svg width="16" height="16" viewBox="0 0 21 21" fill="none" aria-hidden="true">
            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
          </svg>
          Sign in with Microsoft
        </button>
      {/if}

      <div class="chatty-links">
        <button type="button" onclick={() => openDashboard("/?signup=1")}>Create account</button>
        <span>•</span>
        <button type="button" onclick={() => openDashboard("/?request_access=1")}>Request access</button>
        <span>•</span>
        <button type="button" onclick={() => openDashboard("/?forgot_password=1")}>Forgot password?</button>
      </div>
    </section>

    <p class="chatty-footer">Powered by <strong>Chatty AI</strong></p>
  </div>
</div>

<style>
  @import "../styles/chatty-theme.css";

  .chatty-login-shell {
    --chatty-purple: #8b5cf6;
    --chatty-purple-deep: #7c3aed;
    --chatty-purple-soft: rgba(139, 92, 246, 0.16);
    --chatty-cyan: #06b6d4;
    --chatty-ink: #1f2937;
    --chatty-muted: #6b7280;
    --chatty-border: #e5e7eb;
    min-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px 24px;
    background:
      radial-gradient(circle at 18% 4%, rgba(192, 132, 252, 0.22), transparent 28%),
      radial-gradient(circle at 82% 14%, rgba(6, 182, 212, 0.14), transparent 30%),
      linear-gradient(180deg, #ffffff 0%, #fafafa 48%, #f8fafc 100%);
    font-family: var(--chatty-font);
    color: var(--chatty-ink);
    box-sizing: border-box;
  }

  .chatty-login-card-wrap {
    width: 100%;
    max-width: 334px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .chatty-brand-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
  }

  .chatty-logo-frame {
    width: 52px;
    height: 52px;
    display: grid;
    place-items: center;
    border-radius: 16px;
    background: linear-gradient(180deg, #ffffff, #fbfaff);
    border: 1px solid rgba(139, 92, 246, 0.12);
    box-shadow: 0 14px 36px rgba(124, 58, 237, 0.15);
  }

  .chatty-logo-frame img {
    width: 44px;
    height: 44px;
    object-fit: contain;
  }

  h1 {
    margin: 0;
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.2;
    background: linear-gradient(135deg, #c084fc, var(--chatty-purple), var(--chatty-cyan));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .chatty-brand-block p {
    margin: 0;
    font-size: 13px;
    color: var(--chatty-muted);
  }

  .chatty-card {
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid var(--chatty-border);
    border-radius: 16px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-shadow: 0 22px 60px rgba(15, 23, 42, 0.08), 0 2px 12px rgba(124, 58, 237, 0.05);
    backdrop-filter: blur(10px);
  }

  .chatty-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  label span {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
  }

  input {
    width: 100%;
    padding: 9px 12px;
    text-align: start;
    font-size: 13px;
    background: #f9fafb;
    border: 1px solid var(--chatty-border);
    border-radius: 10px;
    color: #1f2937;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    box-sizing: border-box;
  }

  input:focus {
    border-color: var(--chatty-purple);
    box-shadow: 0 0 0 3px var(--chatty-purple-soft);
    background: #fff;
  }

  input:disabled {
    opacity: 0.65;
  }

  .chatty-primary,
  .chatty-microsoft {
    width: 100%;
    border-radius: 10px;
    padding: 9px 0;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
  }

  .chatty-primary {
    color: #fff;
    background: linear-gradient(135deg, var(--chatty-purple), var(--chatty-purple-deep));
    border: none;
  }

  .chatty-primary:hover:not(:disabled) {
    box-shadow: 0 8px 18px rgba(139, 92, 246, 0.28);
    transform: translateY(-1px);
  }

  .chatty-primary:disabled,
  .chatty-microsoft:disabled {
    cursor: default;
    opacity: 0.5;
  }

  .chatty-divider {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .chatty-divider span {
    flex: 1;
    height: 1px;
    background: var(--chatty-border);
  }

  .chatty-divider em {
    font-style: normal;
    font-size: 11px;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .chatty-microsoft {
    color: #374151;
    background: #fff;
    border: 1px solid var(--chatty-border);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .chatty-microsoft:hover:not(:disabled) {
    border-color: var(--chatty-purple);
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.14);
  }

  .chatty-error {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #dc2626;
    font-size: 12px;
    text-align: start;
    line-height: 1.35;
  }

  .chatty-error button,
  .chatty-links button {
    border: none;
    background: transparent;
    color: var(--chatty-purple-deep);
    padding: 0;
    font: inherit;
    cursor: pointer;
  }

  .chatty-auth-loading {
    color: #9ca3af;
    font-size: 11px;
    text-align: center;
  }

  .chatty-links {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    flex-wrap: wrap;
    color: #c4b5fd;
    font-size: 11px;
  }

  .chatty-links button:hover,
  .chatty-error button:hover {
    text-decoration: underline;
  }

  .chatty-footer {
    margin: 0;
    color: #9ca3af;
    font-size: 11px;
    text-align: center;
  }

  .chatty-footer strong {
    color: var(--chatty-purple);
    font-weight: 500;
  }
</style>
